import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Users,
  UserPlus,
  Shield,
  Trash2,
  Settings,
  Check,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { format } from 'date-fns';

// Permission categories and items
const PERMISSION_CATEGORIES = {
  Core: [
    { key: 'dashboard', name: 'Dashboard', description: 'View main dashboard' },
    { key: 'screens', name: 'Screens', description: 'View and manage screens' },
    { key: 'screen_connect', name: 'Screen Connect', description: 'Connect physical screens' },
    { key: 'campaigns', name: 'Campaigns', description: 'View and manage campaigns' },
  ],
  Content: [
    { key: 'content', name: 'Content Library', description: 'View and upload content' },
    { key: 'templates', name: 'Templates', description: 'View content templates' },
    { key: 'content_assign', name: 'Content Assignment', description: 'Assign content to screens' },
    { key: 'content_review', name: 'Content Review', description: 'Approve/reject content' },
  ],
  Scheduling: [
    { key: 'schedule', name: 'Schedule', description: 'Manage content schedules' },
    { key: 'advanced_scheduling', name: 'Advanced Scheduling', description: 'Create rule-based schedules' },
  ],
  Analytics: [
    { key: 'analytics', name: 'Analytics', description: 'View analytics dashboard' },
    { key: 'rtb_analytics', name: 'RTB Analytics', description: 'View real-time bidding analytics' },
    { key: 'audience', name: 'Audience Analytics', description: 'View audience demographics' },
    { key: 'recommendations', name: 'Recommendations', description: 'View AI recommendations' },
  ],
  Programmatic: [
    { key: 'programmatic', name: 'Programmatic', description: 'Manage programmatic advertising' },
    { key: 'dsp_partners', name: 'DSP Partners', description: 'Manage DSP integrations' },
  ],
  Billing: [
    { key: 'billing', name: 'Billing', description: 'View invoices and billing' },
    { key: 'usage_billing', name: 'Usage Billing', description: 'Manage usage-based billing' },
  ],
  Settings: [
    { key: 'white_label', name: 'White Label', description: 'Customize branding' },
    { key: 'users', name: 'User Management', description: 'Manage users' },
  ],
};

const DEFAULT_PERMISSIONS_BY_ROLE = {
  admin: ['all'],
  operator: ['dashboard', 'screens', 'screen_connect', 'campaigns', 'content', 'templates', 
             'content_assign', 'content_review', 'schedule', 'advanced_scheduling', 
             'analytics', 'rtb_analytics', 'audience', 'recommendations', 'programmatic', 'usage_billing'],
  client: ['dashboard', 'screens', 'campaigns', 'content', 'templates', 'analytics', 'billing'],
};

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['Core', 'Content']);
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'client',
    company: '',
    permissions: [],
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const userData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        role: formData.role,
        company: formData.company || null,
        permissions: formData.permissions.length > 0 ? formData.permissions : DEFAULT_PERMISSIONS_BY_ROLE[formData.role],
      };

      await api.post('/users', userData);
      toast.success('User created successfully');
      setShowCreateDialog(false);
      resetForm();
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleUpdatePermissions = async () => {
    if (!selectedUser) return;

    try {
      await api.put(`/users/${selectedUser.id}/permissions`, {
        permissions: selectedUser.permissions || [],
      });
      toast.success('Permissions updated successfully');
      setShowPermissionsDialog(false);
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: 'client',
      company: '',
      permissions: [],
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const togglePermission = (key, isForSelectedUser = false) => {
    if (isForSelectedUser && selectedUser) {
      const currentPerms = selectedUser.permissions || [];
      const newPerms = currentPerms.includes(key)
        ? currentPerms.filter(p => p !== key)
        : [...currentPerms, key];
      setSelectedUser({ ...selectedUser, permissions: newPerms });
    } else {
      setFormData(prev => ({
        ...prev,
        permissions: prev.permissions.includes(key)
          ? prev.permissions.filter(p => p !== key)
          : [...prev.permissions, key],
      }));
    }
  };

  const selectAllInCategory = (category, isForSelectedUser = false) => {
    const categoryPerms = PERMISSION_CATEGORIES[category].map(p => p.key);
    
    if (isForSelectedUser && selectedUser) {
      const currentPerms = selectedUser.permissions || [];
      const allSelected = categoryPerms.every(p => currentPerms.includes(p));
      const newPerms = allSelected
        ? currentPerms.filter(p => !categoryPerms.includes(p))
        : [...new Set([...currentPerms, ...categoryPerms])];
      setSelectedUser({ ...selectedUser, permissions: newPerms });
    } else {
      const allSelected = categoryPerms.every(p => formData.permissions.includes(p));
      setFormData(prev => ({
        ...prev,
        permissions: allSelected
          ? prev.permissions.filter(p => !categoryPerms.includes(p))
          : [...new Set([...prev.permissions, ...categoryPerms])],
      }));
    }
  };

  const applyRoleDefaults = () => {
    setFormData(prev => ({
      ...prev,
      permissions: [...DEFAULT_PERMISSIONS_BY_ROLE[prev.role]],
    }));
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'operator': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'client': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      default: return 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20';
    }
  };

  const PermissionSelector = ({ permissions, onToggle, onSelectCategory, isForSelectedUser = false }) => (
    <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
      {Object.entries(PERMISSION_CATEGORIES).map(([category, items]) => {
        const isExpanded = expandedCategories.includes(category);
        const categoryPerms = items.map(p => p.key);
        const selectedCount = categoryPerms.filter(p => permissions.includes(p)).length;
        const allSelected = selectedCount === items.length;

        return (
          <div key={category} className="border border-zinc-800 rounded-lg overflow-hidden">
            <div
              className="flex items-center justify-between p-3 bg-zinc-900 cursor-pointer hover:bg-zinc-800/50"
              onClick={() => toggleCategory(category)}
            >
              <div className="flex items-center gap-2">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-zinc-400" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-zinc-400" />
                )}
                <span className="text-white font-medium">{category}</span>
                <Badge variant="outline" className="text-xs bg-zinc-800 border-zinc-700">
                  {selectedCount}/{items.length}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectCategory(category, isForSelectedUser);
                }}
                className="text-xs text-zinc-400 hover:text-white"
              >
                {allSelected ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            
            {isExpanded && (
              <div className="p-3 space-y-2 bg-zinc-950">
                {items.map((item) => (
                  <div
                    key={item.key}
                    className="flex items-start gap-3 p-2 rounded hover:bg-zinc-900 cursor-pointer"
                    onClick={() => onToggle(item.key, isForSelectedUser)}
                  >
                    <Checkbox
                      checked={permissions.includes(item.key)}
                      onCheckedChange={() => onToggle(item.key, isForSelectedUser)}
                      className="mt-0.5"
                    />
                    <div className="flex-1">
                      <div className="text-white text-sm font-medium">{item.name}</div>
                      <div className="text-zinc-500 text-xs">{item.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>
            User Management
          </h1>
          <p className="text-zinc-400 mt-1">
            Create and manage users with granular permissions
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setShowCreateDialog(true);
          }}
          className="bg-blue-600 hover:bg-blue-500"
          data-testid="create-user-btn"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Create User
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-500/10">
                <Shield className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Admins</p>
                <p className="text-white text-xl font-bold">
                  {users.filter(u => u.role === 'admin').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Users className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Operators</p>
                <p className="text-white text-xl font-bold">
                  {users.filter(u => u.role === 'operator').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Clients</p>
                <p className="text-white text-xl font-bold">
                  {users.filter(u => u.role === 'client').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-zinc-500/10">
                <Users className="w-5 h-5 text-zinc-400" />
              </div>
              <div>
                <p className="text-zinc-400 text-sm">Total Users</p>
                <p className="text-white text-xl font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="glass-card border-zinc-800">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-white">All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-500">Name</TableHead>
                <TableHead className="text-zinc-500">Email</TableHead>
                <TableHead className="text-zinc-500">Role</TableHead>
                <TableHead className="text-zinc-500">Permissions</TableHead>
                <TableHead className="text-zinc-500">Created</TableHead>
                <TableHead className="text-zinc-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} className="border-zinc-800">
                  <TableCell className="text-white font-medium">{u.name}</TableCell>
                  <TableCell className="text-zinc-400">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={getRoleBadgeColor(u.role)}>
                      {u.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {u.permissions?.includes('all') ? (
                        <Badge variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-xs">
                          All Access
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20 text-xs">
                          {u.permissions?.length || 0} permissions
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-sm">
                    {format(new Date(u.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedUser({ ...u, permissions: u.permissions || [] });
                          setShowPermissionsDialog(true);
                        }}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`edit-permissions-${u.id}`}
                      >
                        <Settings className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteUser(u.id)}
                        disabled={u.id === user.id}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-user-${u.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Create New User
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-zinc-800">
              <TabsTrigger value="details" className="data-[state=active]:bg-zinc-700">
                User Details
              </TabsTrigger>
              <TabsTrigger value="permissions" className="data-[state=active]:bg-zinc-700">
                Permissions
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="John Doe"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="john@example.com"
                    data-testid="user-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">Password *</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="••••••••"
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">Company</Label>
                  <Input
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    placeholder="Company Name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    <SelectItem value="admin" className="text-white hover:bg-zinc-800">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-purple-400" />
                        Admin - Full system access
                      </div>
                    </SelectItem>
                    <SelectItem value="operator" className="text-white hover:bg-zinc-800">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-blue-400" />
                        Operator - Manage screens & content
                      </div>
                    </SelectItem>
                    <SelectItem value="client" className="text-white hover:bg-zinc-800">
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-400" />
                        Client - View campaigns & reports
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="permissions" className="mt-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-zinc-400 text-sm">
                  Select which features this user can access
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={applyRoleDefaults}
                  className="border-zinc-700 text-zinc-300"
                >
                  Apply Role Defaults
                </Button>
              </div>
              
              <PermissionSelector
                permissions={formData.permissions}
                onToggle={togglePermission}
                onSelectCategory={selectAllInCategory}
                isForSelectedUser={false}
              />
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => setShowCreateDialog(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              className="bg-blue-600 hover:bg-blue-500"
              data-testid="submit-create-user"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl" style={{ fontFamily: 'Space Grotesk' }}>
              Edit Permissions: {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-800">
              <Badge variant="outline" className={getRoleBadgeColor(selectedUser?.role)}>
                {selectedUser?.role}
              </Badge>
              <span className="text-zinc-400 text-sm">{selectedUser?.email}</span>
            </div>

            {selectedUser?.role === 'admin' ? (
              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-purple-400">
                  Admin users have full access to all features. Permissions cannot be restricted.
                </p>
              </div>
            ) : (
              <PermissionSelector
                permissions={selectedUser?.permissions || []}
                onToggle={togglePermission}
                onSelectCategory={selectAllInCategory}
                isForSelectedUser={true}
              />
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button
              variant="outline"
              onClick={() => {
                setShowPermissionsDialog(false);
                setSelectedUser(null);
              }}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdatePermissions}
              className="bg-blue-600 hover:bg-blue-500"
              disabled={selectedUser?.role === 'admin'}
            >
              <Check className="w-4 h-4 mr-2" />
              Save Permissions
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
